const QODO_MERGE = 'qodo-merge';
const QODO_GEN = 'qodo-gen';
const QODO_INDEX = 'qodo-index';

export const MARKETING_PARAMS = {
  PRODUCT_SOURCE: 'product_source',
  PRODUCT_TYPE: 'product_type',
  DEVICE_ID: 'device_id',
} as const;

export const MARKETING_DEFAULTS = {
  PRODUCT_SOURCE: 'app',
  PRODUCT_TYPE: 'portal',
  QODO_MERGE_PRODUCT_TYPE: 'ide-plugin',
} as const;

export const QUERY_PARAMS = {
  EMAIL: 'email',
  REDIRECT_TO: 'redirectTo',
  REDIRECT_URI: 'redirect_uri',
  TOKEN: '_t',
  AUTH_V1: 'auth_v1',
  AUTH_V2: 'auth_v2',
  PROVIDER: 'provider',
  REPO: 'repo',
  STATE: 'state',
  CODE: 'code',
  CODE_CHALLENGE: 'code_challenge',
  CODE_CHALLENGE_METHOD: 'code_challenge_method',
  CODE_VERIFIER: 'code_verifier',
  // init_client is used to track the source of the user's login (ide/cli/portal)
  INIT_CLIENT: 'init_client',
  INTERNAL_STATUS_CODE: 'internal_status_code',
  SESSION_ID: 'session_id',
  TRACE_ID: 'trace_id',
  // CLI device code, carried across the MT→ST redirect so the ST login leg can
  // adopt it (init_login adopt_user_code) and auto-confirm without re-typing.
  USER_CODE: 'user_code',
  CUSTOM_OOB_METHOD: 'custom_oob_method',
  LOGIN_HINT: 'login_hint',
  RETURN_URL: 'return_url',
  METHOD: 'method',
  ENTERPRISE_USER_NAME: 'enterprise_user_name',
  CLIENT_ID: 'client_id',
  SCOPE: 'scope',
  RESPONSE_TYPE: 'response_type',
  ACCESS_TYPE: 'access_type',
  ORGANIZATION_ID: 'organization_id',
  REFRESH_TOKEN: 'refresh_token',
  GRANT_TYPE: 'grant_type',
  SOURCE: 'source',
  ERROR_CODE: 'error_code',
  STATUS: 'status',
  APP_CONFIG_ID: 'app_config_id',
  INSTALLATION_ID: 'installation_id',
  HOSTING_TYPE: 'hostingType',
  TRAFFIC_SOURCE: 'traffic_source',
  ...MARKETING_PARAMS,
} as const;

export const QUERY_PARAM_VALUES = {
  SOURCE: {
    IDP_INITIATED: 'idp-initiated',
    MARKETPLACE: 'marketplace',
  },
} as const;

export const PARAMS = {
  TEAM_ID: ':teamId',
  USER_ID: ':userId',
  RULE_ID: ':ruleId',
  SKILL_ID: ':skillId',
  REPOSITORY_ID: ':repositoryId',
  FINDING_ID: ':findingId',
  USER_LOGIN: ':userLogin',
};

/**
 * Query parameters that should be preserved during authentication flows
 * These are used for tracking and session management across redirects.
 * `user_code` is intentionally NOT here: it's a possession secret carried only on the
 * one MT→ST landing leg (by appendCliRedirectParams) and scrubbed on arrival, so it must
 * never be re-propagated across subsequent navigations.
 */
export const PRESERVE_AUTH_PARAMS = [
  QUERY_PARAMS.INIT_CLIENT,
  QUERY_PARAMS.TRACE_ID,
  QUERY_PARAMS.SESSION_ID,
] as const;

/**
 * The routes paths.
 * Can be used for constructing the endpoint (specially from the code itself)
 */
export const ROUTE_PATHS = {
  ROOT: '/',
  HOME: 'home',
  PLANS: 'plans',
  TEAM: 'team',
  TEAMS_MANAGEMENT: 'teams-management',
  USERS: 'users',
  ONBOARDING: 'onboarding',
  ORGANIZATION: 'users-management',
  RULES: 'rules',
  SKILLS: 'skills',
  FINDINGS: 'findings',
  WIKI: 'wiki',
  TEAM_PROFILES: 'team-profiles',
  ANALYTICS: 'analytics',
  INSTALLATION: 'installation',
  INTEGRATIONS: 'integrations',
  CONFIGURATION: 'configurations',
  QODO_MERGE,
  QODO_GEN,
  QODO_INDEX,
  EXTENSIONS: 'extensions',
  MONITOR: 'monitor',
  SIGNIN: 'signin',
  EMAIL: 'email',
  SSO: 'sso',
  CHECK_YOUR_EMAIL: 'check-your-email',
  SUCCESS_SIGNIN_EXTERNAL_CLIENT: 'success-signin-external-client',
  AWARE: 'aware',
  AWARE_SUCCESS: 'success',
  AWARE_FAIL: 'fail',
  ERROR: 'error',
  SUCCESS: 'success',
  NEW: 'new',
  GENERATE: 'generate',
  BILLING: 'billing',
  BILLING_ANALYTICS: 'analytics',
  BILLING_TRANSACTION_HISTORY: 'transaction-history',
  BILLING_MANAGE_SUBSCRIPTION: 'manage-subscription',
  ACCOUNT: 'account',
  SETTINGS: 'settings',
  API_KEYS: 'api-keys',
  SANDBOX_CONFIG: 'sandbox-config',
  MANAGEMENT_REPOSITORIES: 'management',
  REPOSITORIES: 'repositories',
  INSTALLATIONS: 'installations',
  REPO_RELATIONSHIPS: 'relationships',
  GITHUB_INSTALLATION: 'github',
  GITHUB_INSTALLATION_STEP_1: '1',
  GITHUB_INSTALLATION_STEP_2: '2',
  GITHUB_INSTALLATION_STEP_3: '3',
  GITHUB_INSTALLATION_STEP_4: '4',
  GITHUB_INSTALLATION_STEP_5: '5',
  GITHUB_INSTALLATION_STEP_6: '6',
  GITHUB_MT_INSTALLATION: 'github-mt',
  GITHUB_MT_INSTALLATION_STEP_1: '1',
  GITHUB_MT_INSTALLATION_STEP_2: '2',
  GITHUB_MT_MARKETPLACE_CALLBACK: 'marketplace-callback',
  AZURE_INSTALLATION: 'azure-installation',
  AZURE_CONSENT_CALLBACK: 'consent-callback',
  AZURE_INSTALLATION_STEP_0: '0',
  AZURE_INSTALLATION_STEP_1: '1',
  AZURE_INSTALLATION_STEP_2: '2',
  AZURE_INSTALLATION_STEP_3: '3',
  AZURE_INSTALLATION_STEP_4: '4',
  AZURE_INSTALLATION_STEP_5: '5',
  BITBUCKET_INSTALLATION: 'bitbucket',
  BITBUCKET_INSTALLATION_STEP_1: '1',
  BITBUCKET_INSTALLATION_STEP_2: '2',
  BITBUCKET_INSTALLATION_STEP_3: '3',
  BITBUCKET_INSTALLATION_STEP_4: '4',
  BITBUCKET_INSTALLATION_STEP_5: '5',
  BITBUCKET_INSTALLATION_STEP_6: '6',
  GERRIT_INSTALLATION: 'gerrit',
  GERRIT_INSTALLATION_STEP_1: '1',
  GERRIT_INSTALLATION_STEP_2: '2',
  GERRIT_INSTALLATION_STEP_3: '3',
  GITLAB_INSTALLATION: 'gitlab',
  GITLAB_MT_INSTALLATION: 'gitlab-mt',
  GITLAB_INSTALLATION_STEP_1: '1',
  GITLAB_INSTALLATION_STEP_2: '2',
  GITLAB_INSTALLATION_STEP_3: '3',
  GITLAB_INSTALLATION_STEP_4: '4',
  GITLAB_INSTALLATION_STEP_5: '5',
  GITLAB_INSTALLATION_STEP_6: '6',
  GITLAB_INSTALLATION_STEP_7: '7',
  GITLAB_INSTALLATION_CONFIGURE_TOKEN: 'configure-token',
  // v2 wizard steps — nested under the same /integrations/gitlab parent (no "v2"
  // in the URL); descriptive segments so they never collide with v1's 1..7.
  GITLAB_V2_CONNECT: 'connect',
  GITLAB_V2_SELECT: 'select',
  GITLAB_V2_INSTALL: 'install',
} as const;

/**
 * The full routes.
 */
export const ROUTES = {
  ROOT: ROUTE_PATHS.ROOT,
  HOME: ROUTE_PATHS.HOME,
  PLANS: ROUTE_PATHS.PLANS,
  TEAM: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.TEAM}`,
  USERS: `${ROUTE_PATHS.TEAM}/${PARAMS.TEAM_ID}/${ROUTE_PATHS.USERS}`,
  ONBOARDING: ROUTE_PATHS.ONBOARDING,
  ORGANIZATION: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.ORGANIZATION}`,
  RULES: `/${ROUTE_PATHS.RULES}`,
  RULES_NEW: `/${ROUTE_PATHS.RULES}/${ROUTE_PATHS.NEW}`,
  RULES_EDIT: `/${ROUTE_PATHS.RULES}/${PARAMS.RULE_ID}`,
  SKILLS_EDIT: `/${ROUTE_PATHS.RULES}/${ROUTE_PATHS.SKILLS}/${PARAMS.SKILL_ID}`,
  FINDINGS: `/${ROUTE_PATHS.FINDINGS}`,
  FINDINGS_VIEW: `/${ROUTE_PATHS.FINDINGS}/${PARAMS.FINDING_ID}`,
  WIKI: `/${ROUTE_PATHS.WIKI}`,
  WIKI_REPOSITORY: `/${ROUTE_PATHS.WIKI}/${PARAMS.REPOSITORY_ID}`,
  TEAM_PROFILES: `/${ROUTE_PATHS.TEAM_PROFILES}`,
  TEAM_PROFILE_VIEW: `/${ROUTE_PATHS.TEAM_PROFILES}/${PARAMS.USER_LOGIN}`,
  ORGANIZATION_USER_SIDEPANEL: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.ORGANIZATION}/${ROUTE_PATHS.USERS}/${PARAMS.USER_ID}`,
  TEAMS_MANAGEMENT: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.TEAMS_MANAGEMENT}`,
  ANALYTICS: `/${ROUTE_PATHS.ANALYTICS}`,
  BILLING: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.BILLING}`,
  BILLING_ANALYTICS: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.BILLING}/${ROUTE_PATHS.BILLING_ANALYTICS}`,
  BILLING_TRANSACTION_HISTORY: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.BILLING}/${ROUTE_PATHS.BILLING_TRANSACTION_HISTORY}`,
  BILLING_MANAGE_SUBSCRIPTION: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.BILLING}/${ROUTE_PATHS.BILLING_MANAGE_SUBSCRIPTION}`,
  SETTINGS: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.SETTINGS}`,
  API_KEYS: `/${ROUTE_PATHS.ACCOUNT}/${ROUTE_PATHS.API_KEYS}`,
  SANDBOX_CONFIG: `/${ROUTE_PATHS.SANDBOX_CONFIG}`,
  INTEGRATIONS: `/${ROUTE_PATHS.INTEGRATIONS}`,
  INDEX_MONITOR: `/${ROUTE_PATHS.MONITOR}`,
  CONFIGURATION: `/${ROUTE_PATHS.CONFIGURATION}`,
  SIGNIN: `/${ROUTE_PATHS.SIGNIN}`,
  EMAIL: `/${ROUTE_PATHS.SIGNIN}/${ROUTE_PATHS.EMAIL}`,
  SSO: `/${ROUTE_PATHS.SIGNIN}/${ROUTE_PATHS.SSO}`,
  CHECK_YOUR_EMAIL: `/${ROUTE_PATHS.SIGNIN}/${ROUTE_PATHS.CHECK_YOUR_EMAIL}`,
  SUCCESS_SIGNIN_EXTERNAL_CLIENT: `/${ROUTE_PATHS.SIGNIN}/${ROUTE_PATHS.SUCCESS_SIGNIN_EXTERNAL_CLIENT}`,
  AWARE: `/${ROUTE_PATHS.AWARE}`,
  AWARE_SUCCESS: `/${ROUTE_PATHS.AWARE}/${ROUTE_PATHS.AWARE_SUCCESS}`,
  AWARE_FAIL: `/${ROUTE_PATHS.AWARE}/${ROUTE_PATHS.AWARE_FAIL}`,
  ERROR: `/${ROUTE_PATHS.ERROR}`,
  SUCCESS: `/${ROUTE_PATHS.SUCCESS}`,
  MANAGEMENT_REPOSITORIES: `/${ROUTE_PATHS.MANAGEMENT_REPOSITORIES}`,
  INSTALLATIONS: `/${ROUTE_PATHS.MANAGEMENT_REPOSITORIES}/${ROUTE_PATHS.INSTALLATIONS}`,
  REPOSITORIES: `/${ROUTE_PATHS.MANAGEMENT_REPOSITORIES}/${ROUTE_PATHS.REPOSITORIES}`,
  REPOSITORIES_SIDEPANEL: `/${ROUTE_PATHS.MANAGEMENT_REPOSITORIES}/${ROUTE_PATHS.REPOSITORIES}/${PARAMS.REPOSITORY_ID}`,
  // TODO: Remove QODO_MERGE, QODO_GEN, QODO_INDEX ITEM_IDs once deprecated
  // https://linear.app/qodo/issue/PLT-2593/fe-remove-qodo-merge-qodo-gen-qodo-index-routes-once-deprecated
  QODO_MERGE: `/${ROUTE_PATHS.QODO_MERGE}`,
  QODO_MERGE_INSTALLATION: `/${ROUTE_PATHS.QODO_MERGE}/${ROUTE_PATHS.INSTALLATION}`,
  QODO_MERGE_CONFIGURATION: `/${ROUTE_PATHS.QODO_MERGE}/${ROUTE_PATHS.CONFIGURATION}`,
  QODO_MERGE_ANALYTICS: `/${ROUTE_PATHS.QODO_MERGE}/${ROUTE_PATHS.ANALYTICS}`,
  QODO_GEN: `/${ROUTE_PATHS.QODO_GEN}`,
  QODO_INDEX: `/${ROUTE_PATHS.QODO_INDEX}`,
  GITHUB_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}`,
  GITHUB_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}/${ROUTE_PATHS.GITHUB_INSTALLATION_STEP_1}`,
  GITHUB_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}/${ROUTE_PATHS.GITHUB_INSTALLATION_STEP_2}`,
  GITHUB_INSTALLATION_STEP_3: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}/${ROUTE_PATHS.GITHUB_INSTALLATION_STEP_3}`,
  GITHUB_INSTALLATION_STEP_4: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}/${ROUTE_PATHS.GITHUB_INSTALLATION_STEP_4}`,
  GITHUB_INSTALLATION_STEP_5: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}/${ROUTE_PATHS.GITHUB_INSTALLATION_STEP_5}`,
  GITHUB_INSTALLATION_STEP_6: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_INSTALLATION}/${ROUTE_PATHS.GITHUB_INSTALLATION_STEP_6}`,
  GITHUB_MT_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_MT_INSTALLATION}`,
  GITHUB_MT_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_MT_INSTALLATION}/${ROUTE_PATHS.GITHUB_MT_INSTALLATION_STEP_1}`,
  GITHUB_MT_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_MT_INSTALLATION}/${ROUTE_PATHS.GITHUB_MT_INSTALLATION_STEP_2}`,
  GITHUB_MT_MARKETPLACE_CALLBACK: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITHUB_MT_INSTALLATION}/${ROUTE_PATHS.GITHUB_MT_MARKETPLACE_CALLBACK}`,
  AZURE_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}`,
  AZURE_INSTALLATION_STEP_0: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}/${ROUTE_PATHS.AZURE_INSTALLATION_STEP_0}`,
  AZURE_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}/${ROUTE_PATHS.AZURE_INSTALLATION_STEP_1}`,
  AZURE_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}/${ROUTE_PATHS.AZURE_INSTALLATION_STEP_2}`,
  AZURE_INSTALLATION_STEP_3: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}/${ROUTE_PATHS.AZURE_INSTALLATION_STEP_3}`,
  AZURE_INSTALLATION_STEP_4: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}/${ROUTE_PATHS.AZURE_INSTALLATION_STEP_4}`,
  AZURE_INSTALLATION_STEP_5: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.AZURE_INSTALLATION}/${ROUTE_PATHS.AZURE_INSTALLATION_STEP_5}`,
  AZURE_CONSENT_CALLBACK: `/${ROUTE_PATHS.INTEGRATIONS}/azure/${ROUTE_PATHS.AZURE_CONSENT_CALLBACK}`,
  BITBUCKET_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}`,
  BITBUCKET_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}/${ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_1}`,
  BITBUCKET_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}/${ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_2}`,
  BITBUCKET_INSTALLATION_STEP_3: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}/${ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_3}`,
  BITBUCKET_INSTALLATION_STEP_4: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}/${ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_4}`,
  BITBUCKET_INSTALLATION_STEP_5: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}/${ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_5}`,
  BITBUCKET_INSTALLATION_STEP_6: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.BITBUCKET_INSTALLATION}/${ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_6}`,
  GERRIT_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GERRIT_INSTALLATION}`,
  GERRIT_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GERRIT_INSTALLATION}/${ROUTE_PATHS.GERRIT_INSTALLATION_STEP_1}`,
  GERRIT_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GERRIT_INSTALLATION}/${ROUTE_PATHS.GERRIT_INSTALLATION_STEP_2}`,
  GERRIT_INSTALLATION_STEP_3: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GERRIT_INSTALLATION}/${ROUTE_PATHS.GERRIT_INSTALLATION_STEP_3}`,
  GITLAB_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}`,
  GITLAB_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_1}`,
  GITLAB_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_2}`,
  GITLAB_INSTALLATION_STEP_3: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_3}`,
  GITLAB_INSTALLATION_STEP_4: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_4}`,
  GITLAB_INSTALLATION_STEP_5: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_5}`,
  GITLAB_INSTALLATION_STEP_6: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_6}`,
  GITLAB_INSTALLATION_STEP_7: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_7}`,
  GITLAB_INSTALLATION_CONFIGURE_TOKEN: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_CONFIGURE_TOKEN}`,
  GITLAB_V2_CONNECT: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_V2_CONNECT}`,
  GITLAB_V2_SELECT: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_V2_SELECT}`,
  GITLAB_V2_INSTALL: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_INSTALLATION}/${ROUTE_PATHS.GITLAB_V2_INSTALL}`,
  GITLAB_MT_INSTALLATION: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}`,
  GITLAB_MT_INSTALLATION_STEP_1: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_1}`,
  GITLAB_MT_INSTALLATION_STEP_2: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_2}`,
  GITLAB_MT_INSTALLATION_STEP_3: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_3}`,
  GITLAB_MT_INSTALLATION_STEP_4: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_4}`,
  GITLAB_MT_INSTALLATION_STEP_5: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_5}`,
  GITLAB_MT_INSTALLATION_STEP_6: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_STEP_6}`,
  GITLAB_MT_INSTALLATION_CONFIGURE_TOKEN: `/${ROUTE_PATHS.INTEGRATIONS}/${ROUTE_PATHS.GITLAB_MT_INSTALLATION}/${ROUTE_PATHS.GITLAB_INSTALLATION_CONFIGURE_TOKEN}`,
};
